import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/joy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import * as Diff from 'diff';

/**
 * Unified diff viewer using the 'diff' package (like git diff --unified).
 * Shows a proper unified diff between current and proposed content.
 */
export const DiffViewer = ({ current, proposed, onApprove, onDiscard, onCancel }) => {
  const oldText = current || '';
  const newText = proposed || '';

  // Compute unified patch (like git diff)
  const patch = Diff.createPatch(
    'article.md',
    oldText,
    newText,
    'Current',
    'Proposed',
    { context: 3 }
  );

  // Colorize the unified diff output
  const renderUnifiedDiff = (patchText) => {
    const lines = patchText.split('\n');
    return lines.map((line, idx) => {
      let color = 'inherit';
      let bg = 'transparent';
      if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
        color = '#8ab4f8';
        bg = 'rgba(138, 180, 248, 0.08)';
      } else if (line.startsWith('+')) {
        color = '#4ade80';
        bg = 'rgba(74, 222, 128, 0.12)';
      } else if (line.startsWith('-')) {
        color = '#fb7185';
        bg = 'rgba(251, 113, 133, 0.12)';
      }
      return (
        <Box
          key={idx}
          component="div"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            lineHeight: 1.45,
            whiteSpace: 'pre',
            background: bg,
            color,
          }}
        >
          {line || ' '}
        </Box>
      );
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h4" sx={{ color: 'text.primary' }}>
          Differences (Unified Diff)
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="solid"
            color="success"
            startDecorator={<CheckIcon />}
            onClick={() => onApprove(proposed)}
          >
            Approve & Apply
          </Button>
          <Button
            variant="outlined"
            color="danger"
            startDecorator={<CloseIcon />}
            onClick={onDiscard}
          >
            Discard
          </Button>
          <Button variant="plain" onClick={onCancel}>
            Keep Editing in Chat
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 1,
          maxHeight: 560,
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          lineHeight: 1.45,
        }}
      >
        {renderUnifiedDiff(patch)}
      </Box>

      <Typography level="body3" sx={{ color: 'text.secondary', mt: 2 }}>
        Green lines = added in proposed. Red lines = removed from current. @@ = hunk headers. Review before approving.
      </Typography>
    </Box>
  );
};
